/**
 * Opens a URL in a new tab, but only if it is one a browser should navigate to.
 *
 * <p>Ten places in this app hand window.open a URL that did not come from the source: a link
 * fetched from our API, a field on a row, a blob built from a download. window.open honours
 * `javascript:` and `data:` URLs, so any one of those values reaching it becomes script execution
 * in the user's session -- the value never passes through Angular's sanitiser, because window.open
 * is not a template binding.
 *
 * <p>Rather than auditing every call site now and every new one later, they all go through here.
 *
 * <p>`noopener` is always set: without it the opened page gets a handle on this one through
 * window.opener and can navigate it somewhere else.
 */
const ALLOWED_PROTOCOLS = ['http:', 'https:', 'blob:'];

export function isSafeUrl(url: string | null | undefined): boolean {
    if (!url) {
        return false;
    }
    try {
        // Relative urls are resolved against this origin, which is what makes them safe by
        // definition; an absolute javascript: url keeps its own protocol and fails the check.
        return ALLOWED_PROTOCOLS.includes(new URL(url, window.location.origin).protocol);
    } catch {
        return false;
    }
}

/**
 * @returns the window that was opened, or null when the url was refused or the browser blocked it.
 */
export function openExternalUrl(url: string | null | undefined, target = '_blank'): Window | null {
    if (!isSafeUrl(url)) {
        console.error('Refused to open an unsafe url', url);
        return null;
    }
    return window.open(url as string, target, 'noopener');
}
